import React, { useMemo, useState } from 'react';
import { Box, Button, ButtonGroup, Typography } from '@material-ui/core';

import { OutlineButton, PrimaryButton, SecondaryButton } from '../../_core/_ui/buttons.component';
import { FormHelperText } from '../../_core/_ui/forms.component';
import {
  evaluatePlayerMilestones,
  IMilestoneEvaluation,
  IMilestoneInput,
  imperialToMetric,
  isMilestoneInputEmpty,
  metricToImperial,
  roundMilestoneForStorage,
} from '../../../lib/helpers/player-milestone.helpers';
import { MeasurementSystem, PLAYER_MILESTONE_TYPE_FORM_OPTIONS } from '../../../lib/types';
import { MilestoneDisplayRow } from './milestone-display-row.component';
import { MilestoneField } from './milestone-field.component';

interface IPlayerMilestoneSectionProps {
  milestones?: IPlayerMilestone[];
  onRecord: (input: { type: PlayerMilestoneType; value: number }) => Promise<unknown>;
}

const MILESTONE_TYPES = PLAYER_MILESTONE_TYPE_FORM_OPTIONS.map((option) => option.value as PlayerMilestoneType);
const EMPTY_INPUT: IMilestoneInput = { value: '', inches: '' };

const emptyInputs = () => {
  const seed = {} as Record<PlayerMilestoneType, IMilestoneInput>;
  MILESTONE_TYPES.forEach((type) => { seed[type] = EMPTY_INPUT; });
  return seed;
};

// Editor fields for a stored metric value in the active unit system; the metric
// branch is the value itself at storage precision, imperial delegates to the converter.
const toFieldValues = (type: PlayerMilestoneType, metricValue: number | null, system: MeasurementSystem): IMilestoneInput => {
  if (metricValue == null) return EMPTY_INPUT;
  if (system === 'imperial') return metricToImperial(type, metricValue);
  return { value: String(roundMilestoneForStorage(type, metricValue)), inches: '' };
};

const toMetricValue = (type: PlayerMilestoneType, input: IMilestoneInput, system: MeasurementSystem): number =>
  system === 'imperial' ? imperialToMetric(type, input) : Number(input.value);

// Height/weight tracker. Defaults to a read-only "metric / imperial" display;
// Edit flips to editable fields with a metric/imperial toggle. Storage is always
// metric — imperial input is converted on Save. Only fields changed from their
// latest reading are validated (numeric, > 0) and recorded.
export const PlayerMilestoneSection: React.FC<IPlayerMilestoneSectionProps> = ({ milestones, onRecord }) => {
  const byType = useMemo(() => {
    const map = new Map<PlayerMilestoneType, IMilestoneEvaluation>();
    evaluatePlayerMilestones(milestones || []).forEach((evaluation) => map.set(evaluation.type, evaluation));
    return map;
  }, [milestones]);

  const baseline = useMemo(() => {
    const seed = {} as Record<PlayerMilestoneType, number | null>;
    MILESTONE_TYPES.forEach((type) => {
      const evaluation = byType.get(type);
      seed[type] = evaluation ? evaluation.latest.value : null;
    });
    return seed;
  }, [byType]);

  const [editing, setEditing] = useState(false);
  const [system, setSystem] = useState<MeasurementSystem>('metric');
  const [inputs, setInputs] = useState<Record<PlayerMilestoneType, IMilestoneInput>>(emptyInputs);
  const [touched, setTouched] = useState<Partial<Record<PlayerMilestoneType, boolean>>>({});
  const [errors, setErrors] = useState<Partial<Record<PlayerMilestoneType, string>>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showSaved, setShowSaved] = useState(false);

  const seedInputs = (sys: MeasurementSystem) => {
    const seed = {} as Record<PlayerMilestoneType, IMilestoneInput>;
    MILESTONE_TYPES.forEach((type) => { seed[type] = toFieldValues(type, baseline[type], sys); });
    return seed;
  };

  const handleEdit = () => {
    setSystem('metric');
    setInputs(seedInputs('metric'));
    setTouched({});
    setErrors({});
    setSubmitError(null);
    setShowSaved(false);
    setEditing(true);
  };

  const handleCancel = () => {
    setEditing(false);
    setErrors({});
    setSubmitError(null);
  };

  const handleSystemChange = (next: MeasurementSystem) => {
    if (next === system) return;
    setInputs((prev) => {
      const converted = {} as Record<PlayerMilestoneType, IMilestoneInput>;
      MILESTONE_TYPES.forEach((type) => {
        if (isMilestoneInputEmpty(type, prev[type], system)) { converted[type] = EMPTY_INPUT; return; }
        // Converting through stored metric precision is lossy: a toggle can round a
        // just-typed value (e.g. 5'11.5" → 6'0"). Accepted — storage is metric.
        const metric = toMetricValue(type, prev[type], system);
        converted[type] = Number.isNaN(metric) ? prev[type] : toFieldValues(type, metric, next);
      });
      return converted;
    });
    setSystem(next);
  };

  const handleChange = (type: PlayerMilestoneType, input: IMilestoneInput) => {
    setInputs((prev) => ({ ...prev, [type]: input }));
    setTouched((prev) => ({ ...prev, [type]: true }));
    setErrors((prev) => ({ ...prev, [type]: undefined }));
  };

  const handleSave = async () => {
    const nextErrors: Partial<Record<PlayerMilestoneType, string>> = {};
    const toRecord: { type: PlayerMilestoneType; value: number }[] = [];

    MILESTONE_TYPES.forEach((type) => {
      // Only the unit toggle changes an untouched field's text; that's not a user
      // edit, so skip it — otherwise imperial round-trip rounding records a no-op.
      if (!touched[type]) return;
      if (isMilestoneInputEmpty(type, inputs[type], system)) return;

      const metric = toMetricValue(type, inputs[type], system);
      if (Number.isNaN(metric) || metric <= 0) {
        nextErrors[type] = 'Enter a number greater than 0.';
        return;
      }

      const stored = roundMilestoneForStorage(type, metric);
      const original = baseline[type];
      if (original != null && roundMilestoneForStorage(type, original) === stored) return;
      toRecord.push({ type, value: stored });
    });

    setErrors(nextErrors);
    setSubmitError(null);
    setShowSaved(false);
    if (Object.keys(nextErrors).length) return;
    if (!toRecord.length) { setEditing(false); return; }

    setSaving(true);
    try {
      await Promise.all(toRecord.map((record) => onRecord(record)));
      setShowSaved(true);
      setEditing(false);
      setTimeout(() => setShowSaved(false), 2000);
    } catch (err) {
      console.error('recordPlayerMilestones failed:', err);
      setSubmitError("Couldn't save those measurements. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <Box>
        {MILESTONE_TYPES.map((type) => (
          <MilestoneDisplayRow key={type} type={type} metricValue={baseline[type]} evaluation={byType.get(type)} />
        ))}
        <Box display="flex" alignItems="center" mt={2} style={{ gap: 16 }}>
          <OutlineButton onClick={handleEdit}>Edit measurements</OutlineButton>
          {showSaved && <FormHelperText className="success">Saved</FormHelperText>}
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={1} style={{ gap: 12 }}>
        <Typography variant="body2" color="textSecondary">Units</Typography>
        <ButtonGroup size="small" disableElevation>
          <Button variant={system === 'metric' ? 'contained' : 'outlined'} onClick={() => handleSystemChange('metric')}>
            Metric
          </Button>
          <Button variant={system === 'imperial' ? 'contained' : 'outlined'} onClick={() => handleSystemChange('imperial')}>
            Imperial
          </Button>
        </ButtonGroup>
      </Box>
      {MILESTONE_TYPES.map((type) => (
        <MilestoneField
          key={type}
          type={type}
          system={system}
          input={inputs[type] ?? EMPTY_INPUT}
          error={errors[type]}
          evaluation={byType.get(type)}
          onChange={(input) => handleChange(type, input)}
        />
      ))}
      <Box display="flex" alignItems="center" mt={2} style={{ gap: 16 }}>
        <PrimaryButton disabled={saving} onClick={handleSave}>
          {saving ? 'Saving...' : 'Save'}
        </PrimaryButton>
        <SecondaryButton disabled={saving} onClick={handleCancel}>Cancel</SecondaryButton>
        {submitError && <FormHelperText error>{submitError}</FormHelperText>}
      </Box>
    </Box>
  );
};
